import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import GuidesTab from '../../components/admin/guides/GuidesTab';
import {
  useInfiniteGuides,
  GuidesFilters,
  useDeleteGuide,
  usePublishGuide,
  useUnpublishGuide as useUnpublishGuideMutation,
} from '@/hooks/useGuides';
import { useDebounce } from '@/hooks/useDebounce';
import { Guide } from '@/services/apis/guides';

const GuidesContainer: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [selectedProductId, setSelectedProductId] = useState<string | undefined>();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);

  const filters: GuidesFilters = useMemo(
    () => ({
      limit: 10,
      language: 'en',
      productId: selectedProductId === 'all' ? undefined : selectedProductId,
      categoryId: selectedCategoryId === 'all' ? undefined : selectedCategoryId,
      isPublished: selectedStatus === 'published' ? true : selectedStatus === 'draft' ? false : undefined,
      search: debouncedSearch || undefined,
      useDraftData: true,
    }),
    [selectedProductId, selectedCategoryId, selectedStatus, debouncedSearch],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isRefetching, error } =
    useInfiniteGuides(filters);

  const guides = useMemo(() => {
    return (
      data?.pages.flatMap((page) => {
        return page.data || [];
      }) || []
    );
  }, [data]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loaderRef = useInfiniteScroll({
    hasMore: !!hasNextPage && !isFetchingNextPage,
    onLoadMore: handleLoadMore,
  });

  const deleteGuideMutation = useDeleteGuide();
  const publishGuideMutation = usePublishGuide(selectedGuide?.id || '');
  const unpublishGuideMutation = useUnpublishGuideMutation(selectedGuide?.id || '');

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedProductId(undefined);
    setSelectedCategoryId(undefined);
    setSelectedStatus(undefined);
  }, []);

  const handleEdit = useCallback(
    (guide: Guide) => {
      navigate(`/admin/guides/builder/${guide.id}`);
    },
    [navigate],
  );

  const handleDelete = useCallback((guide: Guide) => {
    setSelectedGuide(guide);
    setDeleteModalOpen(true);
  }, []);

  const handlePublish = useCallback((guide: Guide) => {
    setSelectedGuide(guide);
    setPublishModalOpen(true);
  }, []);

  const handleUnpublish = useCallback((guide: Guide) => {
    setSelectedGuide(guide);
    setUnpublishModalOpen(true);
  }, []);

  const confirmDelete = async () => {
    if (!selectedGuide) return;
    try {
      await deleteGuideMutation.mutateAsync(selectedGuide.id);
      addToast({
        title: t('guides.actions.deleteSuccess'),
        color: 'success',
      });
      setDeleteModalOpen(false);
      setSelectedGuide(null);
    } catch (error) {
      addToast({
        title: t('guides.actions.deleteError'),
        color: 'danger',
      });
    }
  };

  const confirmPublish = async () => {
    if (!selectedGuide) return;
    try {
      await publishGuideMutation.mutateAsync();
      setPublishModalOpen(false);
      setSelectedGuide(null);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const confirmUnpublish = async () => {
    if (!selectedGuide) return;
    try {
      await unpublishGuideMutation.mutateAsync();
      setUnpublishModalOpen(false);
      setSelectedGuide(null);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const hasActiveFilters = !!(selectedProductId || selectedCategoryId || selectedStatus !== undefined || searchQuery);

  return (
    <GuidesTab
      guides={guides}
      isLoading={isLoading || isRefetching}
      isFetchingNextPage={isFetchingNextPage}
      error={error?.message || null}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      selectedProductId={selectedProductId}
      onProductChange={setSelectedProductId}
      selectedCategoryId={selectedCategoryId}
      onCategoryChange={setSelectedCategoryId}
      selectedStatus={selectedStatus}
      onStatusChange={setSelectedStatus}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onPublish={handlePublish}
      onUnpublish={handleUnpublish}
      deleteModalOpen={deleteModalOpen}
      publishModalOpen={publishModalOpen}
      unpublishModalOpen={unpublishModalOpen}
      selectedGuide={selectedGuide}
      onDeleteConfirm={confirmDelete}
      onPublishConfirm={confirmPublish}
      onUnpublishConfirm={confirmUnpublish}
      onDeleteModalClose={() => {
        setDeleteModalOpen(false);
        setSelectedGuide(null);
      }}
      onPublishModalClose={() => {
        setPublishModalOpen(false);
        setSelectedGuide(null);
      }}
      onUnpublishModalClose={() => {
        setUnpublishModalOpen(false);
        setSelectedGuide(null);
      }}
      isDeleting={deleteGuideMutation.isPending}
      isPublishing={publishGuideMutation.isPending}
      isUnpublishing={unpublishGuideMutation.isPending}
      loadMoreRef={loaderRef}
      hasNextPage={!!hasNextPage}
    />
  );
};

export default GuidesContainer;
